import type { FollowingState } from "../../../components/MapLibre/LineFollower";
import { NavigationMode } from "../../../types/Navigation";
import { defineEphemeralSlice } from "../runtime/defineEphemeralSlice";

/** Session navigation state (mode, camera follow, GPS line matching). */
export interface NavigationSession {
    "navigationMode":        NavigationMode;
    "isFollowingNavigation": boolean;

    /**
     * When true, the map camera continuously centers on the selected vehicle position.
     * Cleared on user map gesture, vehicle deselection, GPS navigation follow, or FAB GPS.
     */
    "isFollowingVehicle": boolean;

    /**
     * Realtime `trip_id` of the currently selected vehicle, if any. Lets the
     * itinerary renderer hide the leg whose segment is replaced by the realtime
     * transit path.
     */
    "selectedVehicleTripId": string | null;

    /**
     * Id of the itinerary leg currently replaced by realtime mode (vehicle
     * auto-selected by the itinerary hook), if any.
     */
    "realtimeItineraryLegId": string | null;

    /**
     * Stop id of the claimed leg's destination (`leg.to.stop.id`). The realtime
     * transit path is truncated at this stop instead of running to the trip
     * terminus. Only set while {@link realtimeItineraryLegId} is claimed.
     */
    "realtimeItineraryDestinationStopId": string | null;

    /** Parent stop id of the claimed leg's destination, for stop resolution. */
    "realtimeItineraryDestinationStopParent": string | null;

    /**
     * True while the itinerary auto-select is resolving the active leg. During
     * that window the leg segment may be optimistically hidden before the
     * realtime vehicle is confirmed.
     */
    "realtimeItineraryLegPending": boolean;

    /**
     * Realtime `trip_id` tracked for the in-progress itinerary leg, in every
     * tracking phase (selected, dismissed, blocked). Drives the enlarged sprite
     * so the in-progress vehicle stays easy to spot on the map.
     */
    "trackedItineraryTripId": string | null;
    "routeLineFollowing":     FollowingState | null;
}

/** Default navigation session state. */
export const NAVIGATION_SESSION_DEFAULT: NavigationSession = {
    "navigationMode":                         NavigationMode.EXPLORATION,
    "isFollowingNavigation":                  false,
    "isFollowingVehicle":                     false,
    "selectedVehicleTripId":                  null,
    "realtimeItineraryLegId":                 null,
    "realtimeItineraryDestinationStopId":     null,
    "realtimeItineraryDestinationStopParent": null,
    "realtimeItineraryLegPending":            false,
    "trackedItineraryTripId":                 null,
    "routeLineFollowing":                     null,
};

/** Active navigation session (mode, follow camera, GPS line matching). */
export const navigationSessionSlice = defineEphemeralSlice<NavigationSession>( {
    "key":     "navigationSession",
    "default": () => NAVIGATION_SESSION_DEFAULT,
} );
